import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { DashboardFilterInput } from '@/graphql/generated';

export type DashboardFilterContextValue = {
  selectedPropertyId: string | null;
  selectedPropertyLabel: string | null;
  setPropertySelection: (id: string | null, label: string | null) => void;
  geoCountryId: string | null;
  geoStateId: string | null;
  geoCityId: string | null;
  setGeoCountryId: (id: string | null) => void;
  setGeoStateId: (id: string | null) => void;
  setGeoCityId: (id: string | null) => void;
  /** Sustituye país/estado/ciudad de una vez (p. ej. al cerrar el modal del dashboard). */
  replaceGeoFilter: (countryId: string | null, stateId: string | null, cityId: string | null) => void;
  clearGeoFilter: () => void;
  /** Año del período (solo año o año+mes). Sin `year` no se envía período al API. */
  periodYear: number | null;
  /** Mes 1–12 o null = todo el año (requiere `periodYear`). */
  periodMonth: number | null;
  replacePeriodFilter: (year: number | null, month: number | null) => void;
  clearPeriodFilter: () => void;
  dashboardFilter: DashboardFilterInput | undefined;
};

const DashboardFilterContext = createContext<DashboardFilterContextValue | null>(null);

export const DashboardFilterProvider = ({ children }: { children: ReactNode }) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedPropertyLabel, setSelectedPropertyLabel] = useState<string | null>(null);
  const [geoCountryId, setGeoCountryIdState] = useState<string | null>(null);
  const [geoStateId, setGeoStateIdState] = useState<string | null>(null);
  const [geoCityId, setGeoCityIdState] = useState<string | null>(null);
  const [periodYear, setPeriodYearState] = useState<number | null>(null);
  const [periodMonth, setPeriodMonthState] = useState<number | null>(null);

  const setPropertySelection = useCallback((id: string | null, label: string | null) => {
    setSelectedPropertyId(id);
    setSelectedPropertyLabel(label);
  }, []);

  const setGeoCountryId = useCallback((id: string | null) => {
    setGeoCountryIdState(id);
    setGeoStateIdState(null);
    setGeoCityIdState(null);
  }, []);

  const setGeoStateId = useCallback((id: string | null) => {
    setGeoStateIdState(id);
    setGeoCityIdState(null);
  }, []);

  const setGeoCityId = useCallback((id: string | null) => {
    setGeoCityIdState(id);
  }, []);

  const clearGeoFilter = useCallback(() => {
    setGeoCountryIdState(null);
    setGeoStateIdState(null);
    setGeoCityIdState(null);
  }, []);

  const replaceGeoFilter = useCallback(
    (countryId: string | null, stateId: string | null, cityId: string | null) => {
      setGeoCountryIdState(countryId);
      setGeoStateIdState(stateId);
      setGeoCityIdState(cityId);
    },
    [],
  );

  const replacePeriodFilter = useCallback((year: number | null, month: number | null) => {
    if (year == null) {
      setPeriodYearState(null);
      setPeriodMonthState(null);
      return;
    }
    setPeriodYearState(year);
    if (month != null && month >= 1 && month <= 12) {
      setPeriodMonthState(month);
    } else {
      setPeriodMonthState(null);
    }
  }, []);

  const clearPeriodFilter = useCallback(() => {
    setPeriodYearState(null);
    setPeriodMonthState(null);
  }, []);

  const dashboardFilter = useMemo((): DashboardFilterInput | undefined => {
    const hasProperty = Boolean(selectedPropertyId);
    const hasGeo = Boolean(geoCountryId || geoStateId || geoCityId);
    const hasPeriod = periodYear != null;
    if (!hasProperty && !hasGeo && !hasPeriod) return undefined;
    return {
      ...(hasProperty && selectedPropertyId ? { propertyId: selectedPropertyId } : {}),
      ...(geoCountryId ? { countryId: geoCountryId } : {}),
      ...(geoStateId ? { stateId: geoStateId } : {}),
      ...(geoCityId ? { cityId: geoCityId } : {}),
      ...(hasPeriod ? { year: periodYear } : {}),
      ...(hasPeriod && periodMonth != null && periodMonth >= 1 && periodMonth <= 12
        ? { month: periodMonth }
        : {}),
    };
  }, [selectedPropertyId, geoCountryId, geoStateId, geoCityId, periodYear, periodMonth]);

  const value = useMemo(
    () => ({
      selectedPropertyId,
      selectedPropertyLabel,
      setPropertySelection,
      geoCountryId,
      geoStateId,
      geoCityId,
      setGeoCountryId,
      setGeoStateId,
      setGeoCityId,
      replaceGeoFilter,
      clearGeoFilter,
      periodYear,
      periodMonth,
      replacePeriodFilter,
      clearPeriodFilter,
      dashboardFilter,
    }),
    [
      selectedPropertyId,
      selectedPropertyLabel,
      setPropertySelection,
      geoCountryId,
      geoStateId,
      geoCityId,
      setGeoCountryId,
      setGeoStateId,
      setGeoCityId,
      replaceGeoFilter,
      clearGeoFilter,
      periodYear,
      periodMonth,
      replacePeriodFilter,
      clearPeriodFilter,
      dashboardFilter,
    ],
  );

  return (
    <DashboardFilterContext.Provider value={value}>{children}</DashboardFilterContext.Provider>
  );
};

export const useDashboardFilter = () => {
  const ctx = useContext(DashboardFilterContext);
  if (!ctx) {
    throw new Error('useDashboardFilter must be used within DashboardFilterProvider');
  }
  return ctx;
};
